import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Alert from "../../../Common/Alert";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import TreeView from "../../../Common/TreeView";
import { ChartOfAccountStyles } from "./ChartOfAccountShared";

/**
 * Structural view of the chart: the same hierarchy as the list page, without
 * the type filters, for reviewing how the tree is laid out.
 */
const Tree = () => {
  const location = useLocation();
  const [receiveData, setReceiveData] = useState([]);
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await Http.get("/tree-chart-of-account-code");
      setReceiveData(res.data.chart_of_accounts || []);
    } catch (err) {
      console.error("Fetch error:", err);
      setError(
        "Could not load the account structure. Please refresh or try again."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const showAlert = (message, type) => {
    setAlert({ message: message, type: type });
    setTimeout(() => setAlert(null), 4000);
  };

  useEffect(() => {
    if (!location.state) {
      return;
    }
    showAlert(location.state.message, location.state.type);
  }, [location]);

  // Deepest nesting level, so the toolbar can say how tall the tree is.
  const depth = useMemo(() => {
    const walk = (nodes, level = 1) =>
      (nodes || []).reduce(
        (max, node) => Math.max(max, walk(node.children_recursive, level + 1)),
        level - 1
      );
    return walk(receiveData);
  }, [receiveData]);

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">Account Structure</h5>
            <p className="text-muted mb-0 tx-12">
              The chart of accounts hierarchy, {depth}{" "}
              {depth === 1 ? "level" : "levels"} deep
            </p>
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary"
              onClick={fetchData}
              title="Reload data"
            >
              <i className="fa fa-refresh"></i>
            </button>
            <Link to="/chart-of-account" className="btn btn-outline-secondary">
              <i className="fa fa-filter mg-r-6"></i>Filterable List
            </Link>
            <Link to="/create-chart-of-account" className="btn btn-primary">
              <i className="fa fa-plus-square mg-r-6"></i>New Account
            </Link>
          </div>
        </div>

        <div className="am-pagebody">
          <Alert alert={alert} />

          {error && (
            <div className="coa-notice is-alert">
              <i className="fa fa-exclamation-triangle"></i>
              <span className="flex-grow-1">{error}</span>
              <button
                className="btn btn-outline-danger btn-sm"
                onClick={fetchData}
              >
                Retry
              </button>
            </div>
          )}

          {loading ? (
            <div className="coa-panel">
              <div className="coa-empty">
                <i className="fa fa-spinner fa-spin"></i>
                <h6>Loading account structure</h6>
                <p className="mb-0 tx-12">
                  Please wait while the account tree is fetched.
                </p>
              </div>
            </div>
          ) : (
            <TreeView data={receiveData} actionTemplate={actionTemplate} />
          )}
        </div>
      </div>
    </div>
  );
};

const actionTemplate = (item) => (
  <div className="d-flex gap-1 justify-content-center coa-no-print">
    <Link
      to={{ pathname: "/edit-chart-of-account/" + item.id }}
      className="btn btn-outline-secondary btn-sm"
      title="Edit account"
    >
      <i className="fa fa-pencil" aria-hidden="true"></i>
    </Link>
    <Link
      to={{ pathname: "/chart-of-account-ledger/" + item.id }}
      className="btn btn-outline-secondary btn-sm"
      title="View ledger"
    >
      <i className="fa fa-list" aria-hidden="true"></i>
    </Link>
  </div>
);

export default Tree;
